// composables/useCatalogFilters.js
import { ref, computed, watch, onUnmounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useCatalogFilterTokens } from '@/composables/useCatalogFilterTokens';

export const CATALOG_CONFIG = {
  new: { basePath: '/cars', apiBase: 'new', hasTransport: true },
  used: { basePath: '/used-cars', apiBase: 'used', hasTransport: true },
  stock: { basePath: '/cars-in-stock', apiBase: 'stock', hasTransport: true },
  buses: { basePath: '/buses', apiBase: 'buses', hasTransport: false },
};

export const DEFAULT_TRANSPORT = 'passenger';

// Derives the catalog `type` from a route path alone (e.g. `/cars/tm-toyota`
// -> `new`), so callers that render before the catalog block's own data
// resolves (see Page.vue) can identify which CATALOG_CONFIG entry applies
// without waiting on the CMS `sections` payload.
export function getCatalogTypeFromPath(path) {
  const root = `/${path.replace(/^\//, '').split('/')[0]}`;
  const entry = Object.entries(CATALOG_CONFIG).find(
    ([, cfg]) => cfg.basePath === root,
  );
  return entry?.[0] ?? null;
}

/*
  Parses the current URL path into filter state.
  Supports both new/used/buses and stock URL formats.
 
  new/used/buses: /tm-{brand}/{bodytype}/{transmission}/{wheel_drive}/{fuel}/{model}/price-{from}-{to}/sort-{value}?page=N
  stock:          /tm-{brand}/{bodytype}/{transmission}/{wheel_drive}/{fuel}/{model}/{color}/{region}/{dealer}/price-{from}-{to}/sort-{value}?page=N
 */
function parseUrlToFilters(pathSegments, query, type, TOKENS) {
  const filters = {
    brand: [],
    transport: 'passenger', // DEFAULT_TRANSPORT
    bodytype: [],
    transmission: [],
    wheel_drive: [],
    fuel: [],
    model: [],
    color: [],
    region: [],
    dealer: [],
    price_from: null,
    price_to: null,
    sort: null,
    page: query.page ? parseInt(query.page) : 1,
  };

  const segments = [...pathSegments];
  if (!segments.length) return filters;

  for (let i = segments.length - 1; i >= 0; i--) {
    const seg = segments[i];

    if (seg.startsWith('tm-')) {
      filters.brand = seg.replace('tm-', '').split('-').filter(Boolean);
      segments.splice(i, 1);
    } else if (seg.startsWith('price-')) {
      const parts = seg.replace('price-', '').split('-');
      filters.price_from = parts[0] ? parseInt(parts[0]) : null;
      filters.price_to = parts[1] ? parseInt(parts[1]) : null;
      segments.splice(i, 1);
    } else if (seg.startsWith('sort-')) {
      filters.sort = seg.replace('sort-', '');
      segments.splice(i, 1);
    }
  }

  const tokenMap = {
    transport: TOKENS.transport || [],
    bodytype: TOKENS.bodytype || [],
    transmission: TOKENS.transmission || [],
    fuel: TOKENS.fuel || [],
    wheel_drive: TOKENS.wheelDrive || [],
    model: TOKENS.models || [],
    color: TOKENS.colors || [],
    region: TOKENS.regions || [],
    dealer: TOKENS.dealers || [],
  };

  segments.forEach((seg) => {
    let foundMatch = false;

    for (const [field, allowedValues] of Object.entries(tokenMap)) {
      const parts = seg.split('-');

      const matchedValues = allowedValues.filter((token) => {
        return (
          seg === token ||
          seg.includes(`-${token}`) ||
          seg.includes(`${token}-`)
        );
      });

      if (matchedValues.length > 0) {
        if (field === 'transport') {
          filters.transport = matchedValues[0];
        } else {
          filters[field] = [...new Set([...filters[field], ...matchedValues])];
        }
        foundMatch = true;

        break;
      }
    }
  });

  return filters;
}

function buildUrlPath(filters, type, includeTransport = true) {
  const segments = [];

  if (filters.brand.length) {
    segments.push('tm-' + filters.brand.join('-'));
  }

  if (
    includeTransport &&
    CATALOG_CONFIG[type]?.hasTransport &&
    filters.transport &&
    filters.transport !== DEFAULT_TRANSPORT
  ) {
    segments.push(filters.transport);
  }

  const appendMulti = (field) => {
    if (filters[field]?.length) segments.push(filters[field].join('-'));
  };

  appendMulti('bodytype');
  appendMulti('transmission');
  appendMulti('wheel_drive');
  appendMulti('fuel');
  appendMulti('model');

  if (type === 'stock') {
    appendMulti('color');
    appendMulti('region');
    appendMulti('dealer');
  }

  if (filters.price_from != null || filters.price_to != null) {
    segments.push(
      `price-${filters.price_from ?? ''}-${filters.price_to ?? ''}`,
    );
  }

  if (filters.sort) {
    segments.push(`sort-${filters.sort}`);
  }

  return segments.join('/');
}

function buildApiPath(filters, type) {
  const config = CATALOG_CONFIG[type];
  const transportPart = config.hasTransport ? `/${filters.transport}` : '';
  const filterPath = buildUrlPath(filters, type, false);

  return `/api/catalog/${config.apiBase}${transportPart}${filterPath ? '/' + filterPath : ''}`;
}

export function useCatalogFilters(type) {
  const { tokens } = useCatalogFilterTokens();

  const route = useRoute();
  const router = useRouter();
  const config = CATALOG_CONFIG[type];

  const getSegmentsFromRoute = () => {
    const path = route.path.replace(config.basePath, '').replace(/^\//, '');
    return path ? path.split('/') : [];
  };

  const filters = ref(
    parseUrlToFilters(getSegmentsFromRoute(), route.query, type, tokens.value),
  );

  // Draft filters - used on mobile before submit
  const draftFilters = ref({
    ...filters.value,
    brand: [...filters.value.brand],
  });

  const pageUrl = computed(() => {
    const path = buildUrlPath(filters.value, type);
    const base = config.basePath;
    return path ? `${base}/${path}` : base;
  });

  const apiUrl = computed(() => {
    const path = buildApiPath(filters.value, type);
    const query = filters.value.page > 1 ? `?page=${filters.value.page}` : '';
    return path + query;
  });
  const draftApiUrl = computed(() => {
    const path = buildApiPath(draftFilters.value, type);
    return path;
  });

  const activeFilterCount = computed(() => {
    const f = filters.value;
    return [
      f.brand.length,
      f.bodytype.length,
      f.transmission.length,
      f.wheel_drive.length,
      f.fuel.length,
      f.model.length,
      f.color.length,
      f.region.length,
      f.dealer.length,
      f.price_from != null || f.price_to != null ? 1 : 0,
    ].reduce((a, b) => a + b, 0);
  });

  const activeFilterTags = computed(() => {
    const f = filters.value;
    const tags = [];

    const addTags = (field) => {
      (f[field] || []).forEach((val) => {
        tags.push({ field, value: val });
      });
    };

    addTags('brand', (v) => v);
    addTags('bodytype', (v) => v);
    addTags('transmission', (v) => v);
    addTags('wheel_drive', (v) => v);
    addTags('fuel', (v) => v);
    addTags('model', (v) => v);
    addTags('color', (v) => v);
    addTags('region', (v) => v);
    addTags('dealer', (v) => v);

    if (f.price_from != null || f.price_to != null) {
      tags.push({
        field: 'price',
        value: 'price',
      });
    }

    return tags;
  });

  function setFilter(field, value, mode = 'desktop') {
    const target = mode === 'mobile' ? draftFilters : filters;
    const next = { ...target.value };

    if (
      field === 'transport' ||
      field === 'sort' ||
      field === 'price_from' ||
      field === 'price_to'
    ) {
      next[field] = value;
      next.page = 1;
    } else {
      if (Array.isArray(value)) {
        next[field] = value;
      } else {
        const arr = [...(next[field] || [])];
        const idx = arr.indexOf(value);
        if (idx === -1) arr.push(value);
        else arr.splice(idx, 1);
        next[field] = arr;
      }
      next.page = 1;
    }

    // Push the URL first: the catalog fetch is wired to a reactive key
    // that Nuxt watches with a synchronous, unbatched flush, so assigning
    // `target.value` below fires the request immediately. If pushState ran
    // after that assignment, the request would read the browser's
    // still-stale previous URL (useRequest sends it as the `url` param).
    if (mode === 'desktop') {
      _pushToRouter(next);
    }
    target.value = next;
  }

  function buildClearedFilters(transport = filters.value.transport) {
    return {
      transport,
      brand: [],
      bodytype: [],
      transmission: [],
      wheel_drive: [],
      fuel: [],
      model: [],
      color: [],
      region: [],
      dealer: [],
      price_from: null,
      price_to: null,
      sort: null,
      page: 1,
    };
  }

  function setTransport(value) {
    const next = buildClearedFilters(value);
    draftFilters.value = { ...next, brand: [] };
    _pushToRouter(next);
    filters.value = next;
  }

  function setSort(value) {
    const next = { ...filters.value, sort: value, page: 1 };
    _pushToRouter(next);
    filters.value = next;
  }

  function setPage(page) {
    const next = { ...filters.value, page };
    _pushToRouter(next);
    filters.value = next;
  }

  function removeFilterTag(field, value) {
    const next = { ...filters.value };
    if (field === 'price') {
      next.price_from = null;
      next.price_to = null;
    } else {
      const arr = [...(next[field] || [])];
      const idx = arr.indexOf(value);
      if (idx !== -1) arr.splice(idx, 1);
      next[field] = arr;
    }
    next.page = 1;
    _pushToRouter(next);
    filters.value = next;
  }

  function clearFilters() {
    const next = buildClearedFilters();
    draftFilters.value = { ...next, brand: [] };
    _pushToRouter(next);
    filters.value = next;
  }
  function initDraft() {
    draftFilters.value = {
      ...filters.value,
      brand: [...filters.value.brand],
      bodytype: [...filters.value.bodytype],
      transmission: [...filters.value.transmission],
      wheel_drive: [...filters.value.wheel_drive],
      fuel: [...filters.value.fuel],
      model: [...(filters.value.model || [])],
      color: [...(filters.value.color || [])],
      region: [...(filters.value.region || [])],
      dealer: [...(filters.value.dealer || [])],
    };
  }

  function cancelDraft() {
    initDraft();
  }

  function submitDraft() {
    const next = { ...draftFilters.value };
    _pushToRouter(next);
    filters.value = next;
  }

  function setDraftFilter(field, value) {
    setFilter(field, value, 'mobile');
  }

  function _pushToRouter(nextFilters) {
    if (!process.client) return;
    const path = buildUrlPath(nextFilters, type, true);
    const fullPath = path ? `${config.basePath}/${path}` : config.basePath;
    const query = nextFilters.page > 1 ? `?page=${nextFilters.page}` : '';

    // Go through vue-router's own history object (still a synchronous
    // history.pushState under the hood) instead of calling
    // window.history.pushState directly. A raw browser-API call is
    // invisible to vue-router, so its internal "current location"
    // bookkeeping goes stale; the next real router.push() (e.g. clicking
    // into a car detail page) then does a defensive replaceState against
    // that stale location, silently overwriting this entry's URL and
    // corrupting back/forward. router.options.history.push() updates that
    // bookkeeping too, so it stays consistent with the real history stack.
    router.options.history.push(fullPath + query, {
      savedFilters: JSON.parse(JSON.stringify(nextFilters)),
    });
  }

  function syncWithMeta(metaFilters) {
    if (!metaFilters) return;

    const newFilters = { ...filters.value };

    const parseToValues = (val) => {
      if (!val) return [];
      if (Array.isArray(val)) return val.map(String);
      if (typeof val === 'string') return val.split(',').filter(Boolean);
      return [String(val)];
    };

    Object.entries(metaFilters).forEach(([key, value]) => {
      if (
        ['price_from', 'price_to', 'sort', 'transport', 'page'].includes(key)
      ) {
        newFilters[key] = value;
        return;
      }

      if (
        Object.prototype.hasOwnProperty.call(newFilters, key) &&
        Array.isArray(newFilters[key])
      ) {
        newFilters[key] = parseToValues(value);
      }
    });

    filters.value = newFilters;
    if (typeof initDraft === 'function') {
      initDraft();
    }
  }
  const getFilterLink = (field, slug) => {
    const mockFilters = JSON.parse(JSON.stringify(filters.value));

    const toggle = (arr, val) => {
      const list = Array.isArray(arr) ? [...arr] : [];
      return list.includes(val)
        ? list.filter((i) => i !== val)
        : [...list, val];
    };

    if (field === 'transport') {
      mockFilters.transport =
        mockFilters.transport === slug ? 'passenger' : slug;
    } else {
      mockFilters[field] = toggle(mockFilters[field], slug);
    }

    const segments = [];
    const config = CATALOG_CONFIG[type] || CATALOG_CONFIG.new;

    if (mockFilters.brand?.length)
      segments.push(`tm-${mockFilters.brand.join('-')}`);
    if (
      config.hasTransport &&
      mockFilters.transport &&
      mockFilters.transport !== 'passenger'
    ) {
      segments.push(mockFilters.transport);
    }

    return segments.length
      ? `${config.basePath}/${segments.join('/')}`
      : config.basePath;
  };

  const hasAnyFilters = (forFilters) => {
    const data = forFilters;
    if (!data) return false;

    const hasOptions = [
      'brand',
      'transport',
      'bodytype',
      'transmission',
      'fuel',
      'wheel_drive',
      'model',
      'color',
      'region',
      'dealer',
    ].some((key) => Array.isArray(data[key]) && data[key].length > 0);

    if (hasOptions) return true;

    if (data.price && typeof data.price === 'object') {
      const { min, max } = data.price;
      if (max > min && max > 0) return true;
    }

    return false;
  };

  // vue-router's own popstate handling updates `route` on browser
  // back/forward *only* when the resolved location actually differs from
  // its current one. Filter navigation goes through
  // router.options.history.push() below specifically to skip vue-router
  // (no remount/transition per filter click) — which leaves
  // router.currentRoute frozen at whatever the last *real* navigation was
  // (e.g. the link click that first landed on /cars). Pressing Back enough
  // times to land exactly back on that frozen URL fires a popstate event
  // vue-router resolves to a route it already believes is current: no
  // change, no remount, so this watcher never fires and the catalog is
  // left showing stale, filtered data with a URL that no longer matches it.
  //
  // Any navigation vue-router itself recognizes as a *path* change already
  // forces a full page remount (catalog routes are a single catch-all
  // `slug` param, and every filter segment lives in that one param, so a
  // path change here always means a params/key change), which re-derives
  // `filters` correctly from scratch via
  // getSegmentsFromRoute()/parseUrlToFilters() at the fresh instance's own
  // setup. This watcher only needs to react when the path is unchanged but
  // the query changed (e.g. `?page=`) — reacting on a path change too is
  // actively harmful: `route.fullPath` updates (and this watcher fires) on
  // the *outgoing* instance a tick before Vue actually tears it down, so it
  // was re-deriving filters and firing a fetch for data the incoming
  // remount's own fresh instance was about to fetch anyway — an extra,
  // real duplicate request per component using this composable (Page.vue's
  // priming and BlockCatalog both do), confirmed via reproduction.
  let lastPath = route.path;
  watch(
    () => route.fullPath,
    () => {
      if (route.path !== lastPath) return;
      const segments = getSegmentsFromRoute();
      filters.value = parseUrlToFilters(
        segments,
        route.query,
        type,
        tokens.value,
      );
      initDraft();
    },
  );

  // Handles the gap described above: only acts when `route.fullPath`
  // (vue-router's, still synchronous/unchanged at this point in the
  // popstate event) already matches the real browser URL, i.e. vue-router
  // is not about to do anything on its own. Any other popstate is a real
  // vue-router navigation already being handled by its own remount, so
  // touching `filters.value` here too would double-fetch and risks
  // interrupting the page transition mid-flight — exactly what an earlier,
  // unconditional version of this listener used to do.
  function handlePopState() {
    if (!window.location.pathname.startsWith(config.basePath)) return;

    const targetFullPath = window.location.pathname + window.location.search;
    if (route.fullPath !== targetFullPath) return;

    const path = window.location.pathname
      .replace(config.basePath, '')
      .replace(/^\//, '');
    const segments = path ? path.split('/') : [];
    const query = Object.fromEntries(
      new URLSearchParams(window.location.search),
    );

    filters.value = parseUrlToFilters(segments, query, type, tokens.value);
    initDraft();
  }

  if (process.client) {
    window.addEventListener('popstate', handlePopState);
    onUnmounted(() => window.removeEventListener('popstate', handlePopState));
  }

  return {
    filters,
    draftFilters,
    pageUrl,
    apiUrl,
    draftApiUrl,
    activeFilterCount,
    activeFilterTags,
    buildUrlPath,
    setFilter,
    setTransport,
    setSort,
    setPage,
    removeFilterTag,
    clearFilters,
    initDraft,
    cancelDraft,
    submitDraft,
    setDraftFilter,
    syncWithMeta,
    getFilterLink,
    hasAnyFilters,
  };
}
